import Phaser from 'phaser';
import { PlayerPosition, ClientGameState, GamePhase } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { PlayerPositionConfig } from '../../../config/positions';

export interface PlayerLabelData {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  hakemBanner: Phaser.GameObjects.Container;
  hakemSymbol: Phaser.GameObjects.Text;
  text: Phaser.GameObjects.Text;
  playerId: string | null;
}

export interface PlayerLabelsCallbacks {
  getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
}

/**
 * Manages player label display and hakem banners.
 */
export class PlayerLabels {
  private scene: Phaser.Scene;
  private callbacks: PlayerLabelsCallbacks;
  private containers: Map<PlayerPosition, PlayerLabelData> = new Map();
  private currentHakemPosition: PlayerPosition | null = null;
  private hakemTweens: Phaser.Tweens.Tween[] = [];

  constructor(scene: Phaser.Scene, callbacks: PlayerLabelsCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.create();
  }

  getContainer(position: PlayerPosition): PlayerLabelData | undefined {
    return this.containers.get(position);
  }

  getCurrentHakemPosition(): PlayerPosition | null {
    return this.currentHakemPosition;
  }

  private create(): void {
    const teamColors: Record<PlayerPosition, { border: number; hex: string }> = {
      [PlayerPosition.SOUTH]: { border: PERSIAN_THEME.colors.teamA, hex: PERSIAN_THEME.hex.teamA },
      [PlayerPosition.NORTH]: { border: PERSIAN_THEME.colors.teamA, hex: PERSIAN_THEME.hex.teamA },
      [PlayerPosition.WEST]: { border: PERSIAN_THEME.colors.teamB, hex: PERSIAN_THEME.hex.teamB },
      [PlayerPosition.EAST]: { border: PERSIAN_THEME.colors.teamB, hex: PERSIAN_THEME.hex.teamB },
    };

    const positions = [PlayerPosition.SOUTH, PlayerPosition.WEST, PlayerPosition.NORTH, PlayerPosition.EAST];

    for (const position of positions) {
      const coord = this.callbacks.getPositionConfig()[position].label;
      const colors = teamColors[position];

      const container = this.scene.add.container(coord.x, coord.y);
      container.setDepth(16);

      const labelBg = this.scene.add.rectangle(0, 0, 90, 26, PERSIAN_THEME.colors.jade, 0.9);
      labelBg.setStrokeStyle(2, colors.border);

      const { banner: hakemBanner, symbol: hakemSymbol } = this.buildHakemBanner(colors.border, colors.hex);
      hakemBanner.setVisible(false);

      const label = this.scene.add
        .text(0, 0, '', {
          fontFamily: PERSIAN_THEME.fonts.body,
          fontSize: '14px',
          color: PERSIAN_THEME.hex.textPrimary,
        })
        .setOrigin(0.5);

      container.add([labelBg, hakemBanner, label]);
      this.containers.set(position, {
        container,
        bg: labelBg,
        hakemBanner,
        hakemSymbol,
        text: label,
        playerId: null,
      });
    }
  }

  /**
   * Reposition labels based on player count.
   */
  reposition(): void {
    const positions = [PlayerPosition.SOUTH, PlayerPosition.WEST, PlayerPosition.NORTH, PlayerPosition.EAST];
    const positionConfig = this.callbacks.getPositionConfig();

    for (const position of positions) {
      const labelData = this.containers.get(position);
      if (labelData) {
        const coord = positionConfig[position].label;
        labelData.container.setPosition(coord.x, coord.y);
      }
    }
  }

  /**
   * Update labels from game state.
   */
  update(state: ClientGameState): void {
    const playerCount = state.playerCount;
    const isTeamMode = playerCount === 4;

    const teamColors: Record<PlayerPosition, string> = {
      [PlayerPosition.SOUTH]: PERSIAN_THEME.hex.teamA,
      [PlayerPosition.NORTH]: PERSIAN_THEME.hex.teamA,
      [PlayerPosition.WEST]: PERSIAN_THEME.hex.teamB,
      [PlayerPosition.EAST]: PERSIAN_THEME.hex.teamB,
    };

    const individualColors = [PERSIAN_THEME.hex.player1, PERSIAN_THEME.hex.player2, PERSIAN_THEME.hex.player3];

    const individualBorderColors = [
      PERSIAN_THEME.colors.player1,
      PERSIAN_THEME.colors.player2,
      PERSIAN_THEME.colors.player3,
    ];

    // Hide all first
    this.containers.forEach(({ container }) => {
      container.setVisible(false);
    });

    // Update player labels
    state.players.forEach((player) => {
      const displayPosition = this.callbacks.toDisplayPosition(player.position);
      const labelData = this.containers.get(displayPosition);
      if (labelData) {
        labelData.playerId = player.id;
        labelData.text.setText(player.name);

        if (isTeamMode) {
          labelData.text.setColor(teamColors[displayPosition]);
        } else {
          const colorIndex =
            displayPosition === PlayerPosition.SOUTH ? 0 : displayPosition === PlayerPosition.WEST ? 1 : 2;
          labelData.text.setColor(individualColors[colorIndex]);
          labelData.bg.setStrokeStyle(2, individualBorderColors[colorIndex]);
        }

        labelData.bg.setSize(100, 24);
        labelData.container.setVisible(true);
      }
    });

    // Update hakem nameplate
    const hakemPlayer = state.players.find((p) => p.id === state.hakemId);
    if (hakemPlayer && state.phase !== GamePhase.SELECTING_HAKEM) {
      const hakemDisplayPos = this.callbacks.toDisplayPosition(hakemPlayer.position);
      this.showHakemNameplate(hakemDisplayPos, state);
    } else {
      this.showHakemNameplate(null, state);
    }
  }

  private showHakemNameplate(position: PlayerPosition | null, state: ClientGameState): void {
    const positionChanged = this.currentHakemPosition !== position;
    if (!positionChanged) return;

    // Stop existing tweens
    this.hakemTweens.forEach((t) => t.stop());
    this.hakemTweens = [];

    // Hide previous hakem banner
    if (this.currentHakemPosition !== null) {
      const prev = this.containers.get(this.currentHakemPosition);
      if (prev) {
        prev.bg.setVisible(true);
        prev.hakemBanner.setVisible(false);
      }
    }

    // Show new hakem banner
    if (position !== null) {
      const current = this.containers.get(position);
      if (current) {
        current.bg.setVisible(false);
        current.hakemBanner.setVisible(true);

        const playerCount = state.players.length;
        const isTeamMode = playerCount === 4;

        if (isTeamMode) {
          const isTeamA = position === PlayerPosition.SOUTH || position === PlayerPosition.NORTH;
          const colorNum = isTeamA ? PERSIAN_THEME.colors.teamA : PERSIAN_THEME.colors.teamB;
          const colorHex = isTeamA ? PERSIAN_THEME.hex.teamA : PERSIAN_THEME.hex.teamB;
          this.updateHakemBannerColor(position, colorNum, colorHex);
        } else {
          const playerColors = [
            { num: PERSIAN_THEME.colors.player1, hex: PERSIAN_THEME.hex.player1 },
            { num: PERSIAN_THEME.colors.player2, hex: PERSIAN_THEME.hex.player2 },
            { num: PERSIAN_THEME.colors.player3, hex: PERSIAN_THEME.hex.player3 },
          ];
          const colorIndex = position === PlayerPosition.SOUTH ? 0 : position === PlayerPosition.WEST ? 1 : 2;
          const color = playerColors[colorIndex];
          this.updateHakemBannerColor(position, color.num, color.hex);
        }

        // Shimmer animation
        const shimmer = current.hakemBanner.getByName('shimmer') as Phaser.GameObjects.Rectangle;
        if (shimmer) {
          const shimmerTween = this.scene.tweens.add({
            targets: shimmer,
            x: { from: -80, to: 80 },
            duration: 2500,
            ease: 'Sine.easeInOut',
            repeat: -1,
            yoyo: true,
          });
          this.hakemTweens.push(shimmerTween);
        }

        // Pulse animation on symbol
        const pulseTween = this.scene.tweens.add({
          targets: current.hakemSymbol,
          scale: { from: 1, to: 1.1 },
          duration: 1500,
          ease: 'Sine.easeInOut',
          repeat: -1,
          yoyo: true,
        });
        this.hakemTweens.push(pulseTween);
      }
    }

    this.currentHakemPosition = position;
  }

  private buildHakemBanner(
    teamColor: number,
    teamHex: string
  ): { banner: Phaser.GameObjects.Container; symbol: Phaser.GameObjects.Text } {
    const banner = this.scene.add.container(0, 0);

    const bodyWidth = 130;
    const bodyHeight = 30;
    const crownHeight = 24;
    const pendantSize = 6;

    // Mask shape
    const maskShape = this.scene.add.graphics();
    maskShape.fillStyle(0xffffff);
    const maskBodyPoints = [
      { x: -bodyWidth / 2 + 4, y: -bodyHeight / 2 },
      { x: bodyWidth / 2 - 4, y: -bodyHeight / 2 },
      { x: bodyWidth / 2, y: 0 },
      { x: bodyWidth / 2 - 4, y: bodyHeight / 2 },
      { x: -bodyWidth / 2 + 4, y: bodyHeight / 2 },
      { x: -bodyWidth / 2, y: 0 },
    ];
    maskShape.fillPoints(maskBodyPoints, true);
    const maskCrownPoints = [
      { x: -40, y: -bodyHeight / 2 },
      { x: 0, y: -bodyHeight / 2 - crownHeight },
      { x: 40, y: -bodyHeight / 2 },
    ];
    maskShape.fillPoints(maskCrownPoints, true);
    maskShape.setVisible(false);

    // Main body
    const body = this.scene.add.graphics();
    body.setName('body');
    body.fillStyle(PERSIAN_THEME.colors.jade, 0.95);
    body.lineStyle(2, teamColor);
    const bodyPoints = [
      { x: -bodyWidth / 2 + 4, y: -bodyHeight / 2 },
      { x: bodyWidth / 2 - 4, y: -bodyHeight / 2 },
      { x: bodyWidth / 2, y: 0 },
      { x: bodyWidth / 2 - 4, y: bodyHeight / 2 },
      { x: -bodyWidth / 2 + 4, y: bodyHeight / 2 },
      { x: -bodyWidth / 2, y: 0 },
    ];
    body.fillPoints(bodyPoints, true);
    body.strokePoints(bodyPoints, true);

    // Crown fill
    const crownFill = this.scene.add.graphics();
    crownFill.fillStyle(PERSIAN_THEME.colors.jade, 0.95);
    const crownPoints = [
      { x: -40, y: -bodyHeight / 2 },
      { x: 0, y: -bodyHeight / 2 - crownHeight },
      { x: 40, y: -bodyHeight / 2 },
    ];
    crownFill.fillPoints(crownPoints, true);

    // Hakem symbol
    const hakemSymbol = this.scene.add
      .text(0, -bodyHeight / 2 - crownHeight * 0.6, '\u062D', {
        fontFamily: PERSIAN_THEME.fonts.farsi,
        fontSize: '18px',
        color: teamHex,
      })
      .setOrigin(0.5)
      .setStroke('#000000', 3);
    hakemSymbol.setName('hakemSymbol');

    // Crown stroke
    const crownStroke = this.scene.add.graphics();
    crownStroke.setName('crownStroke');
    crownStroke.lineStyle(2, teamColor);
    crownStroke.strokePoints(crownPoints, true);

    // Apex diamond
    const apexDiamond = this.scene.add.graphics();
    apexDiamond.setName('apexDiamond');
    apexDiamond.fillStyle(teamColor, 1);
    const apexY = -bodyHeight / 2 - crownHeight;
    const dSize = 5;
    apexDiamond.fillPoints(
      [
        { x: 0, y: apexY - dSize },
        { x: dSize, y: apexY },
        { x: 0, y: apexY + dSize },
        { x: -dSize, y: apexY },
      ],
      true
    );

    // Bottom pendant
    const pendant = this.scene.add.graphics();
    pendant.setName('pendant');
    pendant.fillStyle(teamColor, 0.8);
    const pendantY = bodyHeight / 2;
    pendant.fillPoints(
      [
        { x: 0, y: pendantY },
        { x: pendantSize, y: pendantY + pendantSize },
        { x: 0, y: pendantY + pendantSize * 2 },
        { x: -pendantSize, y: pendantY + pendantSize },
      ],
      true
    );

    // Corner diamonds
    const cornerDiamonds = this.scene.add.graphics();
    cornerDiamonds.setName('cornerDiamonds');
    cornerDiamonds.fillStyle(teamColor, 0.7);
    const cornerSize = 3;
    const cornerOffsets = [
      { x: -bodyWidth / 2 + 14, y: 0 },
      { x: bodyWidth / 2 - 14, y: 0 },
    ];
    for (const offset of cornerOffsets) {
      cornerDiamonds.fillPoints(
        [
          { x: offset.x, y: offset.y - cornerSize },
          { x: offset.x + cornerSize, y: offset.y },
          { x: offset.x, y: offset.y + cornerSize },
          { x: offset.x - cornerSize, y: offset.y },
        ],
        true
      );
    }

    // Shimmer
    const shimmer = this.scene.add.rectangle(-80, 0, 50, bodyHeight + crownHeight + 20, 0xffffff, 0.15);
    shimmer.setName('shimmer');
    const mask = maskShape.createGeometryMask();
    shimmer.setMask(mask);

    banner.add([maskShape, body, crownFill, shimmer, hakemSymbol, crownStroke, apexDiamond, pendant, cornerDiamonds]);

    return { banner, symbol: hakemSymbol };
  }

  private updateHakemBannerColor(position: PlayerPosition, colorNum: number, colorHex: string): void {
    const labelData = this.containers.get(position);
    if (!labelData) return;

    const banner = labelData.hakemBanner;
    const bodyWidth = 130;
    const bodyHeight = 30;
    const crownHeight = 24;
    const pendantSize = 6;
    const dSize = 5;
    const cornerSize = 3;

    // Update body
    const body = banner.getByName('body') as Phaser.GameObjects.Graphics;
    if (body) {
      body.clear();
      body.fillStyle(PERSIAN_THEME.colors.jade, 0.95);
      body.lineStyle(2, colorNum);
      const bodyPoints = [
        { x: -bodyWidth / 2 + 4, y: -bodyHeight / 2 },
        { x: bodyWidth / 2 - 4, y: -bodyHeight / 2 },
        { x: bodyWidth / 2, y: 0 },
        { x: bodyWidth / 2 - 4, y: bodyHeight / 2 },
        { x: -bodyWidth / 2 + 4, y: bodyHeight / 2 },
        { x: -bodyWidth / 2, y: 0 },
      ];
      body.fillPoints(bodyPoints, true);
      body.strokePoints(bodyPoints, true);
    }

    // Update crown stroke
    const crownStroke = banner.getByName('crownStroke') as Phaser.GameObjects.Graphics;
    if (crownStroke) {
      crownStroke.clear();
      crownStroke.lineStyle(2, colorNum);
      const crownPoints = [
        { x: -40, y: -bodyHeight / 2 },
        { x: 0, y: -bodyHeight / 2 - crownHeight },
        { x: 40, y: -bodyHeight / 2 },
      ];
      crownStroke.strokePoints(crownPoints, true);
    }

    // Update apex diamond
    const apexDiamond = banner.getByName('apexDiamond') as Phaser.GameObjects.Graphics;
    if (apexDiamond) {
      apexDiamond.clear();
      apexDiamond.fillStyle(colorNum, 1);
      const apexY = -bodyHeight / 2 - crownHeight;
      apexDiamond.fillPoints(
        [
          { x: 0, y: apexY - dSize },
          { x: dSize, y: apexY },
          { x: 0, y: apexY + dSize },
          { x: -dSize, y: apexY },
        ],
        true
      );
    }

    // Update pendant
    const pendant = banner.getByName('pendant') as Phaser.GameObjects.Graphics;
    if (pendant) {
      pendant.clear();
      pendant.fillStyle(colorNum, 0.8);
      const pendantY = bodyHeight / 2;
      pendant.fillPoints(
        [
          { x: 0, y: pendantY },
          { x: pendantSize, y: pendantY + pendantSize },
          { x: 0, y: pendantY + pendantSize * 2 },
          { x: -pendantSize, y: pendantY + pendantSize },
        ],
        true
      );
    }

    // Update corner diamonds
    const cornerDiamonds = banner.getByName('cornerDiamonds') as Phaser.GameObjects.Graphics;
    if (cornerDiamonds) {
      cornerDiamonds.clear();
      cornerDiamonds.fillStyle(colorNum, 0.7);
      const cornerOffsets = [
        { x: -bodyWidth / 2 + 14, y: 0 },
        { x: bodyWidth / 2 - 14, y: 0 },
      ];
      for (const offset of cornerOffsets) {
        cornerDiamonds.fillPoints(
          [
            { x: offset.x, y: offset.y - cornerSize },
            { x: offset.x + cornerSize, y: offset.y },
            { x: offset.x, y: offset.y + cornerSize },
            { x: offset.x - cornerSize, y: offset.y },
          ],
          true
        );
      }
    }

    // Update symbol color
    labelData.hakemSymbol.setColor(colorHex);
  }

  destroy(): void {
    this.hakemTweens.forEach((t) => t.stop());
    this.hakemTweens = [];
    this.containers.forEach(({ container }) => container.destroy());
    this.containers.clear();
  }
}
